import React, { useMemo, useState } from 'react';
import clsx from 'clsx';
import * as R from 'ramda';

import { Button, Checkbox, Icon } from '@atom/mui';
import { FolderIcon } from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import fonts from '@atom/styles/fonts';
import { InventoryFullCategoryTreeType } from '@atom/types/category';
import { InventorySchemaItem } from '@atom/types/schema';
import { fullCategoryLeftTreeIcon } from '@atom/utilities/iconUtilities';
import schemaUtilities from '@atom/utilities/schemaUtilities';

import classes from './inventoryExportDetail.module.css';

const PADDING_OFFSET = 1.5;

const styles = {
  hoverBtn: {
    color: colors.brand.blue,
    cursor: 'pointer',
    fontWeight: 500,
    fontSize: fonts.xs,
    padding: '0 0.25rem',
    margin: '0 1rem',
  },
};

interface Props {
  category: InventoryFullCategoryTreeType;
  indentation: number;
  rootSchemas: InventorySchemaItem[];
  toggleCategoryId: (categoryId: string, schemaId: string) => void;
  updateCategoryIds: (categoryIds: string[], schemaId: string) => void;
  selectedCategoryIds: Set<string>;
  selectedSchemaId: string;
}

const CategoryTreeRow = ({
  category,
  indentation,
  rootSchemas,
  toggleCategoryId,
  updateCategoryIds,
  selectedCategoryIds,
  selectedSchemaId,
}: Props) => {
  const [allSelected, setAllSelected] = useState(false);
  const [hovering, setHovering] = useState<boolean>(false);

  const colorId = rootSchemas.reduce(
    (acc: any, schema: InventorySchemaItem): any => {
      return schema.id === category.schemaId ? schema.colorId : acc;
    },
    '',
  );

  const schemaColor = schemaUtilities.getSchemaColorFromColorId(colorId);
  const isDisabled = !R.isEmpty(selectedSchemaId)
    ? category.schemaId !== selectedSchemaId
    : false;

  const leftIcon = R.isNil(category.schemaId) ? (
    fullCategoryLeftTreeIcon()
  ) : (
    <>
      <Checkbox
        checked={selectedCategoryIds.has(category.id)}
        onChange={() => toggleCategoryId(category.id, category.schemaId)}
        disabled={isDisabled}
      />
      <Icon
        style={{
          fontSize: '24px',
          marginLeft: R.isNil(category.schemaId) ? '0.5rem' : '0rem',
        }}
        color={schemaColor}
        icon={FolderIcon}
      />
    </>
  );

  const style = {
    position: 'relative',
    paddingLeft: `calc(0.75rem + ${PADDING_OFFSET * indentation}rem)`,
  };

  const colorStyle = {
    color: selectedCategoryIds.has(category.id)
      ? colors.brand.blue
      : colors.neutral.dark,
  };

  const selectedStyle = clsx(
    classes.row,
    selectedCategoryIds.has(category.id) && classes.selected,
  );

  const subText =
    category.assetType && category.schemaId ? category.assetType : '';

  const canSelectAll = useMemo(
    () =>
      // ensure children exist
      category.hasCategoryChildren &&
      // ensure multiple children
      R.pathOr(0, ['children', 'length'], category) > 1 &&
      // ensure all children are leaf nodes with same schema id as first childCategory
      // and a different schema is not already selected
      !category.children.some(childCategory => {
        return (
          childCategory?.hasCategoryChildren === true ||
          childCategory?.schemaId !== category.children[0]?.schemaId ||
          (!R.isEmpty(selectedSchemaId) &&
            childCategory.schemaId !== selectedSchemaId)
        );
      }),
    [category, selectedSchemaId],
  );

  const handleToggleAll = event => {
    event.stopPropagation();
    setAllSelected(!allSelected);
    const childIds = R.map(({ id }) => id, category.children);
    const schemaId = allSelected ? null : category.children[0].schemaId;
    updateCategoryIds(childIds, schemaId);
  };

  const ToggleBox: React.ReactNode = (
    <Button sx={styles.hoverBtn} onClick={handleToggleAll}>
      {allSelected ? 'Des' : 'S'}elect Sub Folders
    </Button>
  );

  return (
    <>
      <div
        className={selectedStyle}
        style={style}
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
      >
        {leftIcon}
        <div className={classes.nameContainer}>
          <div className={classes.categoryRowName} style={colorStyle}>
            {category.name}
            {canSelectAll && hovering && ToggleBox}
          </div>
          <div className={classes.categoryRowSubtext}>{subText}</div>
        </div>
      </div>
      {category.children ? (
        category.children.map((nextCategory: InventoryFullCategoryTreeType) => {
          return (
            <CategoryTreeRow
              key={nextCategory.id}
              category={nextCategory}
              indentation={indentation + 1}
              rootSchemas={rootSchemas}
              toggleCategoryId={toggleCategoryId}
              updateCategoryIds={updateCategoryIds}
              selectedCategoryIds={selectedCategoryIds}
              selectedSchemaId={selectedSchemaId}
            />
          );
        })
      ) : (
        <div />
      )}
    </>
  );
};

export default CategoryTreeRow;
